import { supabase } from "./supabase-client";

// ============================================================
// BANK ACCOUNTS
// ============================================================

export async function getBankAccounts(producerId: string | null, organizationId?: string) {
  const col = organizationId ? "organization_id" : "producer_id";
  const val = (organizationId ?? producerId) as string;
  const { data, error } = await ((supabase.from("bank_accounts") as any)
    .select("*")
    .eq(col, val)
    .order("created_at", { ascending: false }));
  if (error) throw error;
  return data || [];
}

export async function createBankAccount(account: {
  producer_id?: string | null;
  organization_id?: string | null;
  created_by?: string | null;
  account_name: string;
  account_holder_name?: string;
  document?: string;
  account_type?: string;
  bank_name?: string;
  agency?: string;
  account_number?: string;
  pix_key?: string;
  pix_key_type?: string;
  is_default?: boolean;
}) {
  // If setting as default, unset other defaults first (scoped to the same owner)
  if (account.is_default) {
    const col = account.organization_id ? "organization_id" : "producer_id";
    const val = (account.organization_id ?? account.producer_id) as string;
    await (supabase.from("bank_accounts") as any).update({ is_default: false }).eq(col, val);
  }
  const { data, error } = await (supabase.from("bank_accounts") as any)
    .insert(account)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateBankAccount(id: string, updates: Record<string, any>) {
  const { data, error } = await supabase
    .from("bank_accounts")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBankAccount(id: string) {
  const { error } = await supabase.from("bank_accounts").delete().eq("id", id);
  if (error) throw error;
}

// ============================================================
// FINANCIAL TRANSACTIONS
// ============================================================

export async function getFinancialTransactions(producerId: string, filters?: {
  type?: string;
  status?: string;
  event_id?: string;
  category?: string;
  from_date?: string;
  to_date?: string;
}) {
  let query = supabase
    .from("financial_transactions")
    .select("*, events(title), bank_accounts(account_name)")
    .eq("producer_id", producerId)
    .order("created_at", { ascending: false });

  if (filters?.type) query = query.eq("type", filters.type);
  if (filters?.status) query = query.eq("status", filters.status);
  if (filters?.event_id) query = query.eq("event_id", filters.event_id);
  if (filters?.category) query = query.eq("category", filters.category);
  if (filters?.from_date) query = query.gte("due_date", filters.from_date);
  if (filters?.to_date) query = query.lte("due_date", filters.to_date);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function createFinancialTransaction(tx: {
  producer_id: string;
  event_id?: string;
  order_id?: string;
  type: string;
  category: string;
  description: string;
  amount: number;
  status?: string;
  due_date?: string;
  bank_account_id?: string;
  notes?: string;
}) {
  const { data, error } = await supabase
    .from("financial_transactions")
    .insert(tx)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateFinancialTransaction(id: string, updates: Record<string, any>) {
  const { data, error } = await supabase
    .from("financial_transactions")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFinancialTransaction(id: string) {
  const { error } = await supabase.from("financial_transactions").delete().eq("id", id);
  if (error) throw error;
}

// ============================================================
// CASH FLOW SUMMARY
// ============================================================

export async function getCashFlowSummary(producerId: string) {
  const { data, error } = await supabase
    .from("financial_transactions")
    .select("type, status, amount, category, due_date")
    .eq("producer_id", producerId);
  if (error) throw error;

  const items = data || [];
  const receivable = items.filter(i => i.type === "receivable");
  const payable = items.filter(i => i.type === "payable");

  const totalReceivable = receivable.reduce((s, i) => s + Number(i.amount), 0);
  const totalPayable = payable.reduce((s, i) => s + Number(i.amount), 0);
  const confirmedReceivable = receivable.filter(i => i.status === "confirmed" || i.status === "paid").reduce((s, i) => s + Number(i.amount), 0);
  const confirmedPayable = payable.filter(i => i.status === "confirmed" || i.status === "paid").reduce((s, i) => s + Number(i.amount), 0);
  const pendingReceivable = receivable.filter(i => i.status === "pending").reduce((s, i) => s + Number(i.amount), 0);
  const pendingPayable = payable.filter(i => i.status === "pending").reduce((s, i) => s + Number(i.amount), 0);

  return {
    totalReceivable,
    totalPayable,
    confirmedReceivable,
    confirmedPayable,
    pendingReceivable,
    pendingPayable,
    balance: totalReceivable - totalPayable,
    items,
  };
}

// ============================================================
// EVENT RECONCILIATION
// ============================================================

export async function getEventReconciliation(producerId: string, organizationId?: string) {
  let events: any[] = [];
  if (organizationId) {
    const { data, error } = await (supabase as any)
      .from("events")
      .select("id, title, status, visibility, start_date, end_date, platform_fee_percent")
      .eq("organization_id", organizationId)
      .order("start_date", { ascending: false });
    if (error) throw error;
    events = data || [];
  } else {
    const { data: owned, error: ownedError } = await (supabase as any)
      .from("events")
      .select("id, title, status, visibility, start_date, end_date, platform_fee_percent")
      .eq("producer_id", producerId)
      .order("start_date", { ascending: false });
    if (ownedError) throw ownedError;

    let collaborating: any[] = [];
    try {
      const { data: collaboratorRows, error: collabError } = await (supabase as any)
        .from("event_collaborators")
        .select("event_id")
        .eq("user_id", producerId)
        .eq("status", "active");

      if (!collabError && collaboratorRows && collaboratorRows.length > 0) {
        const eventIds = collaboratorRows.map((r) => r.event_id);
        const { data: collabEvents, error: collabEventsError } = await (supabase as any)
          .from("events")
          .select("id, title, status, visibility, start_date, end_date, platform_fee_percent")
          .in("id", eventIds)
          .order("start_date", { ascending: false });

        if (!collabEventsError && collabEvents) {
          collaborating = collabEvents;
        }
      }
    } catch (e) {
      console.error("[getEventReconciliation] collaborator fetch failed:", e);
    }

    const map = new Map<string, any>();
    for (const event of (owned || [])) map.set(event.id, event);
    for (const event of collaborating) map.set(event.id, event);
    events = Array.from(map.values()).sort(
      (a, b) => new Date(b.start_date || 0).getTime() - new Date(a.start_date || 0).getTime()
    );
  }

  if (events.length === 0) return [];

  const eventIds = events.map(e => e.id);

  // Fast SQL aggregation via RPC (returns sum of paid_total, platform_fee, etc. in < 15ms)
  const { data: summaryRows, error: summaryError } = await supabase.rpc("get_producer_events_financial_summary" as any, {
    p_event_ids: eventIds,
  });

  const { data: analytics } = await supabase
    .from("event_analytics")
    .select("event_id, tickets_sold, tickets_checked_in, total_revenue, platform_revenue, producer_revenue")
    .in("event_id", eventIds);

  const summaryMap = new Map((summaryRows || []).map((s: any) => [s.event_id, s]));

  return events.map(event => {
    const sum = summaryMap.get(event.id);
    const eventAnalytics = (analytics || []).find(a => a.event_id === event.id);

    const sumData: any = sum;
    const paidTotal = Number(sumData?.paid_total || 0);
    const platformFees = Number(sumData?.paid_platform_fee || 0);
    const gatewayFees = Number(sumData?.paid_gateway_fee || 0);
    const refunds = Number(sumData?.refunded_total || 0);

    const ticketRevenue = paidTotal - platformFees;
    const netRevenue = ticketRevenue - gatewayFees - refunds;

    return {
      ...event,
      ordersCount: eventAnalytics?.tickets_sold || 0,
      ticketsSold: eventAnalytics?.tickets_sold || 0,
      ticketsCheckedIn: eventAnalytics?.tickets_checked_in || 0,
      ticketRevenue,
      platformFees,
      gatewayFees,
      refunds,
      netRevenue,
    };
  });
}
